import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createActionGroup, emptyProps, props, Store } from '@ngrx/store';
import { createReducer, on, createFeatureSelector, createSelector } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  CategoryTemplateRecursive as ApiCategoryTemplateRecursive,
  PaginatedCategoryTemplateRecursiveList,
  SearchService,
} from '@offconon/core-api';
import { CommonSelectOption } from '@offconon/shared/ui/shared-ui-virtualscroll-multiselect';

export interface CategoryTemplateRecursive extends ApiCategoryTemplateRecursive {
  id: number;
  name?: string;
  keyword_translation?: string;
  parent_id?: number;
  cover_path?: string;
  children?: CategoryTemplateRecursive[];
  category_path?: string[];
}

export interface MappedSystemData {
  id: number;
  parent_id?: number;
  name: string;
  category_path: string[];
  cover_path?: string;
  children: any[];
}

export interface CategorySelectOption extends CommonSelectOption {
  category_path?: string[];
  cover_path?: string;
  children?: any[];
}

export interface CategoryPathState {
  categoryResponse: PaginatedCategoryTemplateRecursiveList | null;
  loading: boolean;
  error?: string;
  selectedCategoryId: number | null;
  mappedCategories: MappedSystemData[];
  categoryType?: string;
  page?: number;
  pageSize?: number;
  searchKeyword?: string;
}

export const initialCategoryPathState: CategoryPathState = {
  categoryResponse: null,
  loading: false,
  error: undefined,
  selectedCategoryId: null,
  mappedCategories: [],
};

export const CategoryPathActions = createActionGroup({
  source: 'Category Path',
  events: {
    'Load Categories': props<{
      page: number;
      pageSize: number;
      searchData?: {
        category_type?: string | string[];
        keyword_translation?: string;
      };
      selectFirst?: boolean;
      appendData?: boolean;
    }>(),
    'Load Categories Success': props<{
      response: PaginatedCategoryTemplateRecursiveList;
      selectFirst: boolean | undefined;
      appendData: boolean | undefined;
    }>(),
    'Load Categories Failure': props<{ error: string }>(),
    'Select Category': props<{ categoryId: number }>(),
    'Create Category': props<{ category: any }>(),
    'Create Category Success': props<{
      category: CategoryTemplateRecursive;
    }>(),
    'Create Category Failure': props<{ error: string }>(),
    'Set Category Type': props<{ categoryType: string }>(),
    'Set Search Params': props<{ page: number; pageSize: number; searchKeyword?: string }>(),
    Reset: emptyProps(),
  },
});

export const categoryPathReducer = createReducer(
  initialCategoryPathState,
  on(CategoryPathActions.loadCategories, (state) => ({
    ...state,
    loading: true,
  })),
  on(CategoryPathActions.loadCategoriesSuccess, (state, { response, appendData }) => {
    const newMappedCategories: MappedSystemData[] = [];

    const processItem = (item: any, path: string[] = []) => {
      const newPath = [...path, item?.keyword_translation];

      const mappedItem: MappedSystemData = {
        id: item?.id,
        parent_id: item?.parent_id,
        name: item?.keyword_translation,
        category_path: newPath,
        cover_path: item?.cover_path,
        children: item?.children || [],
      };

      newMappedCategories.push(mappedItem);

      if (item.children?.length > 0) {
        for (const child of item.children) {
          processItem(child, newPath);
        }
      }
    };

    if (response.results && response.results.length > 0) {
      response.results.forEach((item: any) => processItem(item));
    }

    let combinedMappedCategories: MappedSystemData[];
    let combinedResults: any[];

    if (appendData) {
      // Only append unique categories/results
      const existingIds = state.mappedCategories.map((item) => item.id);
      const uniqueNewCategories = newMappedCategories.filter(
        (item) => !existingIds.includes(item.id),
      );

      combinedMappedCategories = [...state.mappedCategories, ...uniqueNewCategories];

      const existingResults = state.categoryResponse?.results || [];
      const existingResultIds = existingResults.map((item: any) => item.id);
      const uniqueNewResults = (response.results || []).filter(
        (item: any) => !existingResultIds.includes(item.id),
      );
      combinedResults = [...existingResults, ...uniqueNewResults];
    } else {
      // Replace everything with new data
      combinedMappedCategories = [...newMappedCategories];
      combinedResults = [...(response.results || [])];
    }

    const combinedResponse = {
      ...response,
      results: combinedResults,
    };

    return {
      ...state,
      categoryResponse: combinedResponse,
      mappedCategories: combinedMappedCategories,
      loading: false,
      error: undefined,
    };
  }),
  on(CategoryPathActions.loadCategoriesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(CategoryPathActions.selectCategory, (state, { categoryId }) => {
    return {
      ...state,
      selectedCategoryId: categoryId,
    };
  }),
  on(CategoryPathActions.createCategory, (state) => ({
    ...state,
    loading: true,
  })),
  on(CategoryPathActions.createCategorySuccess, (state, { category }) => {
    const updatedResults = state.categoryResponse?.results
      ? [category, ...state.categoryResponse.results]
      : [category];

    const updatedResponse = state.categoryResponse
      ? { ...state.categoryResponse, results: updatedResults }
      : { count: 1, next: undefined, previous: undefined, results: updatedResults };
    const temp: any = category;
    const newMappedCategory: any = {
      id: temp.id,
      parent_id: temp.parent_id,
      name: temp.keyword.translations[0].translation,
      category_path: [temp.keyword.translations[0].translation],
      cover_path: temp.cover_path,
      children: [],
    };
    return {
      ...state,
      categoryResponse: updatedResponse as PaginatedCategoryTemplateRecursiveList,
      mappedCategories: [newMappedCategory, ...state.mappedCategories],
      selectedCategoryId: temp.id,
      loading: false,
    };
  }),
  on(CategoryPathActions.createCategoryFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(CategoryPathActions.setCategoryType, (state, { categoryType }) => ({
    ...state,
    categoryType,
  })),
  on(CategoryPathActions.setSearchParams, (state, { page, pageSize, searchKeyword }) => ({
    ...state,
    page,
    pageSize,
    searchKeyword: searchKeyword || undefined,
  })),
  on(CategoryPathActions.reset, () => initialCategoryPathState),
);

export const selectCategoryPathState = createFeatureSelector<CategoryPathState>('categoryPath');

export const selectCategories = createSelector(
  selectCategoryPathState,
  (state) => state.categoryResponse?.results || [],
);

export const selectMappedCategories = createSelector(
  selectCategoryPathState,
  (state) => state.mappedCategories,
);

export const selectCategoriesCommonSelectOption = createSelector(
  selectMappedCategories,
  (categories): CategorySelectOption[] =>
    categories.map(
      (category): CategorySelectOption => ({
        id: category.id,
        name: category.name,
        additionalData: category,
      }),
    ) || [],
);

export const selectSelectedCategoryId = createSelector(
  selectCategoryPathState,
  (state) => state.selectedCategoryId,
);

export const selectSelectedCategoryCommonSelectOption = createSelector(
  selectCategoryPathState,
  (state): CommonSelectOption | null => {
    if (!state.selectedCategoryId) return null;
    const cat = state.mappedCategories.find((cat) => cat.id === state.selectedCategoryId);
    return cat
      ? {
          id: cat.id,
          name: cat.name,
          additionalData: cat,
        }
      : null;
  },
);

export const selectSelectedCategoryPath = createSelector(selectCategoryPathState, (state) => {
  return state.mappedCategories.find((cat) => cat.id === state.selectedCategoryId);
});

export const selectSelectedCategoryJoinedPath = createSelector(
  selectCategoryPathState,
  (state): string | undefined => {
    if (!state.selectedCategoryId) return undefined;
    const category = state.mappedCategories.find((cat) => cat.id === state.selectedCategoryId);

    if (!category) return undefined;

    const currentPath = category.category_path || [];

    return currentPath.join(' > ');
  },
);

export const selectLoading = createSelector(selectCategoryPathState, (state) => state.loading);

export class CategoryPathEffects {
  private actions$ = inject(Actions);
  private searchService = inject(SearchService);
  private store = inject(Store);

  loadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryPathActions.loadCategories),
      debounceTime(300),
      switchMap(({ page, pageSize, searchData, selectFirst, appendData }) => {
        return this.searchService
          .searchCategoryTemplatePathList(page, pageSize, searchData || {})
          .pipe(
            map((response) => {
              return CategoryPathActions.loadCategoriesSuccess({
                response,
                selectFirst: selectFirst,
                appendData,
              });
            }),
            catchError((error) =>
              of(
                CategoryPathActions.loadCategoriesFailure({
                  error: error.message || 'Failed to load categories',
                }),
              ),
            ),
          );
      }),
    ),
  );

  loadCategoriesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryPathActions.loadCategoriesSuccess),
      withLatestFrom(this.store.select(selectSelectedCategoryId)),
      switchMap(([{ response, selectFirst }, selectedCategoryId]) => {
        if (selectFirst && !selectedCategoryId && response.results && response.results.length > 0) {
          const firstCategory = response.results[0] as any;
          return of(CategoryPathActions.selectCategory({ categoryId: firstCategory.id }));
        }
        return of();
      }),
    ),
  );

  createCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryPathActions.createCategory),
      switchMap(({ category }) => {
        return of(category).pipe(
          map((createdCategory) =>
            CategoryPathActions.createCategorySuccess({
              category: createdCategory as CategoryTemplateRecursive,
            }),
          ),
          catchError((error) =>
            of(
              CategoryPathActions.createCategoryFailure({
                error: error.message || 'Failed to create category',
              }),
            ),
          ),
        );
      }),
    ),
  );
}
